import throttle from 'lodash.throttle'
import { DirectiveBinding, Directive } from 'vue'

interface CustomElement extends HTMLElement {
  _isMousePressed?: boolean;
  _startX?: number;
  _startY?: number;
  _scrollTop?: number;
  _scrollLeft?: number;
  _onMouseMoveCb?: (e: MouseEvent) => void;
  _onMouseDownCb?: (e: MouseEvent) => void;
  _onMouseUpCb?: (e: MouseEvent) => void;
}

export const vDragScroll: Directive = {
  mounted (el: CustomElement, binding: DirectiveBinding) {
    const onMouseMove = (e: MouseEvent) => {
      if (!el._isMousePressed) {
        return
      }

      const x = e.pageX - el.offsetLeft
      const y = e.pageY - el.offsetTop

      const startX = el._startX ?? 0
      const startY = el._startY ?? 0
      const scrollLeft = el._scrollLeft ?? 0
      const scrollTop = el._scrollTop ?? 0

      const walkX = x - startX
      const walkY = y - startY
      el.scrollLeft = scrollLeft - walkX
      el.scrollTop = scrollTop - walkY
    }

    const onMouseDown = (e: MouseEvent) => {
      el.style.cursor = 'grab'

      if (el._onMouseMoveCb) {
        el.addEventListener('mousemove', el._onMouseMoveCb)
      }

      el._isMousePressed = true
      el._startX = e.pageX - el.offsetLeft
      el._startY = e.pageY - el.offsetTop
      el._scrollTop = el.scrollTop
      el._scrollLeft = el.scrollLeft
    }

    const onMouseUp = () => {
      el.style.cursor = 'inherit'

      if (el._onMouseMoveCb) {
        el.removeEventListener('mousemove', el._onMouseMoveCb)
      }

      delete el._isMousePressed
      delete el._startX
      delete el._startY
      delete el._scrollLeft
      delete el._scrollTop
    }

    el._onMouseMoveCb = throttle(onMouseMove, 100)
    el._onMouseDownCb = onMouseDown
    el._onMouseUpCb = onMouseUp

    if (binding.value === false) {
      return
    }

    el.addEventListener('mousedown', onMouseDown)
    el.addEventListener('mouseup', onMouseUp)
  },

  updated (el: CustomElement, binding: DirectiveBinding) {
    const { oldValue, value } = binding
    if (value === true && oldValue === false) {
      if (el._onMouseDownCb) {
        el.addEventListener('mousedown', el._onMouseDownCb)
      }

      if (el._onMouseUpCb) {
        el.addEventListener('mouseup', el._onMouseUpCb)
      }
    } else if (value === false && oldValue === true) {
      if (el._onMouseDownCb) {
        el.removeEventListener('mousedown', el._onMouseDownCb)
      }

      if (el._onMouseUpCb) {
        el.removeEventListener('mouseup', el._onMouseUpCb)
      }

      if (el._onMouseMoveCb) {
        el.removeEventListener('mousemove', el._onMouseMoveCb)
      }

      delete el._isMousePressed
      delete el._startX
      delete el._startY
      delete el._scrollLeft
      delete el._scrollTop
    }
  },

  unmounted (el: CustomElement) {
    if (el._onMouseDownCb) {
      el.removeEventListener('mousedown', el._onMouseDownCb)
    }

    if (el._onMouseUpCb) {
      el.removeEventListener('mouseup', el._onMouseUpCb)
    }

    if (el._onMouseMoveCb) {
      el.removeEventListener('mousemove', el._onMouseMoveCb)
    }

    delete el._isMousePressed
    delete el._startX
    delete el._startY
    delete el._scrollLeft
    delete el._scrollTop
    delete el._onMouseMoveCb
    delete el._onMouseDownCb
    delete el._onMouseUpCb
  },
}
